// The share dialog's image rules: which image actions a browser gets, what the PNG is called, and which
// hint a network target shows. Import-free on purpose (see "Tests (jest)" in orion-web's CLAUDE.md); the
// rendering itself lives in `ShareWinDialog`.
//
// The honest constraint behind all of it: a share-intent URL carries text only, never a file. The ONE web
// path that attaches the card is the Web Share API with files (most mobile browsers, Safari on macOS,
// Chrome on Windows/ChromeOS). Everywhere else the reader gets the image as a download or on the clipboard
// and attaches it to the post by hand, and the copy says so.

export type ShareImageAction = 'share' | 'download' | 'copy-image';

export interface ShareImageCapabilities {
  /** `navigator.share` accepts our PNG (Web Share API Level 2). */
  canShareFiles: boolean;
  /** `navigator.clipboard.write` with an `image/png` ClipboardItem. */
  canCopyImage: boolean;
}

export const NO_IMAGE_CAPABILITIES: ShareImageCapabilities = { canShareFiles: false, canCopyImage: false };

interface NavigatorLike {
  share?: unknown;
  canShare?: unknown;
  clipboard?: { write?: unknown };
}

/**
 * Reads the browser's capabilities. `canShare` has to be asked with the real file: a browser can share
 * files in general and still refuse this type or size. `pngClipboard` is `ClipboardItem.supports('image/png')`
 * where that exists; an older browser with a ClipboardItem and no `supports` gets the benefit of the doubt,
 * and a failed write says so at click time.
 */
export function detectShareImageCapabilities(env: {
  navigator?: NavigatorLike;
  file?: unknown;
  hasClipboardItem: boolean;
  pngClipboard?: boolean;
}): ShareImageCapabilities {
  const nav = env.navigator;
  if (!nav) return NO_IMAGE_CAPABILITIES;
  let canShareFiles = false;
  if (env.file && typeof nav.share === 'function' && typeof nav.canShare === 'function') {
    try {
      canShareFiles = (nav.canShare as (data: { files: unknown[] }) => boolean)({ files: [env.file] }) === true;
    } catch {
      canShareFiles = false;
    }
  }
  const canCopyImage = env.hasClipboardItem && typeof nav.clipboard?.write === 'function' && env.pngClipboard !== false;
  return { canShareFiles, canCopyImage };
}

/** The image actions to offer, in display order. Download is always there: it is the one that works everywhere. */
export function shareImageActions(caps: ShareImageCapabilities): ShareImageAction[] {
  const out: ShareImageAction[] = [];
  if (caps.canShareFiles) out.push('share');
  out.push('download');
  if (caps.canCopyImage) out.push('copy-image');
  return out;
}

/**
 * Where native share with files is missing, a link target also downloads the card, so the image is on
 * disk when the network's composer opens. Where it exists, Share is the path that attaches the image and
 * the links stay text-only, so a tap does not drop a surprise download.
 */
export function linkTargetDownloadsImage(caps: ShareImageCapabilities, imageReady: boolean): boolean {
  return imageReady && !caps.canShareFiles;
}

/**
 * The network link targets show only where native share cannot do the job. On a phone that shares files,
 * Share already reaches every app with the image, and the links there either open nothing or post text only.
 */
export function showLinkTargets(caps: ShareImageCapabilities, narrow: boolean): boolean {
  return !(caps.canShareFiles && narrow);
}

export type ShareTarget ='instagram' | 'linkedin' | 'facebook' | 'x' | 'whatsapp' | 'copy';

/** The hint keys a target click shows, in order. Instagram and Facebook copy the caption; every link target can also download the card. */
export function targetHintKeys(target: ShareTarget, downloadsImage: boolean): string[] {
  const keys: string[] = [];
  if (target === 'instagram') keys.push('wins.share.instagramHint');
  if (target === 'facebook') keys.push('wins.share.facebookHint');
  if (downloadsImage && target !== 'copy') keys.push('wins.share.imageDownloadedHint');
  return keys;
}

function slug(text: string): string {
  return text
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * `symphony-win-1a2b3c4d-2026-10-07.png` / `symphony-recap-week-2026-10-07.png`. The entity is the BI
 * entity the dialog already carries (a win id, or `recap-week` / `recap-month`); a win id is cut to eight
 * characters so the name stays readable. The date is the reader's local day.
 */
export function shareImageFileName(entity: string, productName: string, now: Date): string {
  const product = slug(productName) || 'share';
  const what = entity.startsWith('recap-') ? slug(entity) || 'recap' : ['win', slug(entity).slice(0, 8)].filter(Boolean).join('-');
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return `${product}-${what}-${day}.png`;
}

/** A user cancelling the system share sheet rejects with AbortError; that is not a failure to report. */
export function isShareCancel(error: unknown): boolean {
  return !!error && typeof error === 'object' && (error as { name?: unknown }).name === 'AbortError';
}
